/**
 * useGames Hook
 * React Query hooks for game data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gamesAPI } from '../services/api';

// Query keys
const QUERY_KEYS = {
  games: (params) => ['games', params],
  game: (id) => ['game', id],
  gameByCode: (gameCode, language) => ['gameByCode', gameCode, language],
  feedback: (id) => ['feedback', id]
};

/**
 * Fetch games list with filters
 */
export const useGames = (params = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.games(params),
    queryFn: () => gamesAPI.getAll(params),
    staleTime: 0, // Always consider data stale
    cacheTime: 30000 // Keep in cache for 30 seconds
  });
};

/**
 * Fetch single game by ID
 */
export const useGame = (id) => {
  return useQuery({
    queryKey: QUERY_KEYS.game(id),
    queryFn: () => gamesAPI.getById(id),
    enabled: !!id,
    staleTime: 0, // Always consider data stale
    cacheTime: 30000 // Keep in cache for 30 seconds
  });
};

/**
 * Fetch game by gameCode and language
 * Used to check parent game status for expansions
 */
export const useGameByCode = (gameCode, language = 'en', options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.gameByCode(gameCode, language),
    queryFn: () => gamesAPI.getByCode(gameCode, language),
    enabled: !!gameCode && options.enabled !== false,
    staleTime: 30000, // Cache for 30 seconds
    cacheTime: 60000
  });
};

/**
 * Create new game mutation
 */
export const useCreateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (gameData) => gamesAPI.create(gameData),
    onSuccess: () => {
      // Invalidate games list to refetch
      queryClient.invalidateQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Update game mutation
 */
export const useUpdateGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => gamesAPI.update(id, data),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.game(variables.id) });
      queryClient.refetchQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Delete game mutation
 */
export const useDeleteGame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => gamesAPI.delete(id),
    onSuccess: () => {
      // Invalidate games list to refetch
      queryClient.invalidateQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Transition game status mutation
 */
export const useTransitionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newStatus, targetChannel }) => gamesAPI.transitionStatus(id, newStatus, targetChannel),
    onSuccess: (data, variables) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.game(variables.id) });
      queryClient.refetchQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Cancel pending modifications and restore production version
 */
export const useCancelModifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => gamesAPI.cancelModifications(id),
    onSuccess: (data, id) => {
      // Force refetch instead of just invalidating
      queryClient.refetchQueries({ queryKey: QUERY_KEYS.game(id) });
      queryClient.refetchQueries({ queryKey: ['games'] });
    }
  });
};

/**
 * Fetch beta feedback for a game
 */
export const useGameFeedback = (id, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.feedback(id),
    queryFn: () => gamesAPI.getFeedback(id),
    enabled: !!id && options.enabled !== false,
    staleTime: 60000 // Cache for 1 minute
  });
};
